import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Check, Mic, Image as ImageIcon, MessageCircle, PenLine } from 'lucide-react';
import { useAppStore } from '@/store/appStore';
import { useSubscribeGreekBrain } from '@/hooks/useApi';
import { usePlatform } from '@/hooks/usePlatform';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/utils/translations';
import { appAnalytics } from '@/services/analytics';
import { GreekBrainUsageList } from './GreekBrainUsageList';

/**
 * Subscription paywall for GreekBrain's paid AI features. This is a RECURRING
 * monthly subscription — deliberately separate from the Hallouminati one-time
 * lifetime guide purchase (see PaywallModal.tsx). Driven by global store state so
 * the API client's entitlement handler can open it from anywhere.
 *
 * On iOS the buy itself runs in the native StoreKit sheet
 * (ios/App/App/Paywall/GreekBrainPaywall.swift). Its title, benefits and
 * footer copy are a hand-kept copy of BENEFITS and the strings below — when
 * you change them here, change them there in the same commit.
 */
const BENEFITS: { icon: React.ReactNode; label: string }[] = [
    { icon: <Mic size={14} className="text-green-600 stroke-[3]" />, label: 'Live voice role-play dialogues' },
    { icon: <ImageIcon size={14} className="text-green-600 stroke-[3]" />, label: 'AI feedback on your spoken picture descriptions' },
    { icon: <MessageCircle size={14} className="text-green-600 stroke-[3]" />, label: 'Unlimited AI tutor conversations' },
    { icon: <PenLine size={14} className="text-green-600 stroke-[3]" />, label: 'AI grading of your writing' },
];

export const GreekBrainPaywallModal: React.FC = () => {
    const { open, source, message } = useAppStore((s) => s.greekbrainPaywall);
    const closePaywall = useAppStore((s) => s.closeGreekbrainPaywall);
    const trialRemaining = useAppStore((s) => s.user?.greekbrain_trial_remaining ?? 0);
    const locale = useAppStore((s) => s.user?.locale);
    const { t } = useTranslation((locale as 'en' | 'ru') || 'en');
    const subscribe = useSubscribeGreekBrain();
    const { hapticFeedback } = usePlatform();
    const { showToast } = useToast();

    // Mounted from the moment it opens until the close animation has played.
    const [isMounted, setIsMounted] = useState(false);
    if (open && !isMounted) setIsMounted(true);

    useEffect(() => {
        if (open) {
            appAnalytics.logEvent('View GreekBrain Paywall', { source });
            hapticFeedback.medium();
        } else {
            const timer = setTimeout(() => setIsMounted(false), 300);
            return () => clearTimeout(timer);
        }
    }, [open, source, hapticFeedback]);

    const handleSubscribe = async () => {
        hapticFeedback.selection();
        appAnalytics.logEvent('GreekBrain Subscribe Tapped', { source });

        try {
            // iOS / Android: the native store sheet opens over this modal and
            // resolves when it closes (GreekBrainPaywall.swift mirrors the copy
            // below — keep the two in sync by hand). Web: Stripe Checkout redirect.
            const result = await subscribe.mutateAsync();
            if (result.checkoutUrl) {
                window.location.href = result.checkoutUrl;
                return;
            }
            if (result.cancelled) {
                return;
            }
            if (result.success) {
                hapticFeedback.success();
                showToast('Welcome to GreekBrain AI!', 'success');
                closePaywall();
                return;
            }
            if (result.error) {
                showToast(result.error, 'error');
                hapticFeedback.error();
            }
        } catch (error) {
            console.error('GreekBrain subscription failed:', error);
            showToast('Could not start the subscription. Please try again.', 'error');
            hapticFeedback.error();
        }
    };

    if (!isMounted) return null;

    return (
        <AnimatePresence>
            {open && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
                        onClick={closePaywall}
                    />

                    <div
                        className="min-h-full flex items-center justify-center p-4 pb-32"
                        style={{ paddingTop: 'max(5rem, calc(var(--tg-app-top-inset, env(safe-area-inset-top, 0px)) + 1rem))' }}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            transition={{ type: 'spring', duration: 0.5, bounce: 0.3 }}
                            className="w-full max-w-md relative z-10"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="bg-white rounded-3xl shadow-xl overflow-hidden w-full relative flex flex-col">
                                <button
                                    onClick={closePaywall}
                                    className="absolute top-4 right-4 z-10 p-2 bg-black/5 hover:bg-black/10 rounded-full transition-colors"
                                >
                                    <X size={20} className="text-gray-500" />
                                </button>

                                <div className="pt-8 pb-2 px-6 text-center">
                                    <h2 className="text-2xl font-black text-gray-900 mb-2 leading-tight tracking-tight">
                                        Unlock GreekBrain AI
                                    </h2>
                                    <p className="text-gray-500 text-sm max-w-xs mx-auto leading-relaxed font-medium">
                                        {message || 'Subscribe monthly to practise speaking, get AI feedback, and chat with the tutor.'}
                                    </p>
                                </div>

                                <div className="px-6 py-4 space-y-3 flex-grow">
                                    {BENEFITS.map((benefit, index) => (
                                        <div key={index} className="flex items-start gap-3">
                                            <div className="bg-green-100 rounded-full p-1 mt-0.5 flex-shrink-0">
                                                {benefit.icon}
                                            </div>
                                            <span className="text-gray-700 text-sm font-medium leading-tight pt-0.5">
                                                {benefit.label}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                {/* Trial status + current monthly usage / quotas */}
                                <div className="px-6 pb-2 space-y-3">
                                    {trialRemaining > 0 && (
                                        <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-center text-xs font-semibold text-amber-700">
                                            {trialRemaining} free AI {trialRemaining === 1 ? 'try' : 'tries'} left
                                        </div>
                                    )}
                                    <GreekBrainUsageList title="Monthly quota" />
                                </div>

                                <div className="px-6 pb-6 pt-2 mt-auto text-center">
                                    <button
                                        onClick={handleSubscribe}
                                        disabled={subscribe.isPending}
                                        className="w-full font-bold rounded-2xl h-14 flex justify-center items-center gap-2 text-lg transition-all uppercase tracking-wide cursor-pointer active:translate-y-1 active:shadow-none bg-primary hover:bg-primary-shadow text-white shadow-[0_4px_0_var(--color-primary-shadow)] disabled:opacity-70 disabled:cursor-not-allowed disabled:shadow-none disabled:active:translate-y-0"
                                    >
                                        {subscribe.isPending ? (
                                            <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        ) : (
                                            <span className="whitespace-nowrap">Subscribe monthly</span>
                                        )}
                                    </button>
                                    <p className="text-center text-xs text-gray-400 mt-4 font-medium select-none flex items-center justify-center gap-1.5">
                                        <Check size={12} className="text-gray-400" />
                                        Renews monthly · cancel anytime
                                    </p>
                                    <p className="text-center text-[11px] text-gray-400 mt-2">
                                        {t('dialogue_all_free') || 'Everything else — pictures, vocabulary and practice — stays free.'}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            )}
        </AnimatePresence>
    );
};
